import React, { useEffect, useState } from 'react';
import { Plus, Search, AlertCircle, Circle, X, ChevronDown, Edit, Trash2, FileText, Upload, Building2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';
import { useNavigate } from '../../hooks/useNavigate';
import { ConfirmModal } from '../common/ConfirmModal';
import { useAuth } from '../../contexts/AuthContext';
import { SignedLink } from '../common/SignedFile';

interface Driver {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  cdl_number: string;
  cdl_expiration: string;
  medical_card_expiration: string;
  is_active: boolean;
  driver_status?: 'active' | 'inactive' | 'vacation' | 'on_leave' | 'terminated';
  hire_date: string;
  driver_type: 'company_driver' | 'owner_operator';
  dispatcher_id?: string | null;
  truck?: {
    id: string;
    unit_number: string;
    make: string;
    model: string;
  };
  companies?: {
    name: string;
    code: string;
  };
}


interface DriversViewProps {
  companyFilter?: string;
}

// Format date without timezone conversion (shows exact date from database)
const formatDateNoTZ = (dateString: string | null | undefined): string => {
  if (!dateString) return 'N/A';
  // Parse the date string as-is without timezone conversion
  const [year, month, day] = dateString.split('T')[0].split('-');
  return `${month}/${day}/${year}`;
};

export function DriversView({ companyFilter }: DriversViewProps = {}) {
  const toast = useToast();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDriver, setSelectedDriver] = useState<Driver | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [driverToAction, setDriverToAction] = useState<Driver | null>(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [collapsedCompanies, setCollapsedCompanies] = useState<Set<string>>(new Set());
  const [collapsedStatuses, setCollapsedStatuses] = useState<Set<string>>(new Set());
  const [selectedDriverIds, setSelectedDriverIds] = useState<Set<string>>(new Set());
  const [showMoveToModal, setShowMoveToModal] = useState(false);
  const [terminationLetterModal, setTerminationLetterModal] = useState<{ driverId: string; driverName: string; currentUrl: string | null } | null>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [showOfficeVisitModal, setShowOfficeVisitModal] = useState(false);
  const [officeVisitReason, setOfficeVisitReason] = useState('');

  const [companyNames, setCompanyNames] = useState<Record<string, string>>({});

  // Load company names from database
  useEffect(() => {
    async function loadCompanyNames() {
      const { data } = await supabase
        .from('companies')
        .select('code, name')
        .eq('is_active', true);
      
      if (data) {
        const names: Record<string, string> = {};
        data.forEach(company => {
          names[company.code.toLowerCase()] = company.name;
        });
        setCompanyNames(names);
      }
    }
    loadCompanyNames();
  }, []);

  // Handle column sorting
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Toggle driver selection
  const toggleDriverSelection = (driverId: string) => {
    setSelectedDriverIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(driverId)) {
        newSet.delete(driverId);
      } else {
        newSet.add(driverId);
      }
      return newSet;
    });
  };

  // Select all drivers in current view
  const toggleSelectAll = () => {
    if (selectedDriverIds.size === filteredDrivers.length) {
      setSelectedDriverIds(new Set());
    } else {
      setSelectedDriverIds(new Set(filteredDrivers.map(d => d.id)));
    }
  };

  // Handle termination letter upload
  async function handleTerminationLetterUpload(driverId: string) {
    if (!uploadFile) {
      toast.error('Please select a file to upload');
      return;
    }

    try {
      setUploading(true);
      const fileExt = uploadFile.name.split('.').pop();
      const fileName = `${driverId}-termination-${Date.now()}.${fileExt}`;
      const filePath = `termination-letters/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('company-documents')
        .upload(filePath, uploadFile);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('company-documents')
        .getPublicUrl(filePath);

      const { error: updateError } = await supabase
        .from('drivers')
        .update({ termination_letter_url: publicUrl })
        .eq('id', driverId);

      if (updateError) throw updateError;

      toast.success('Termination letter uploaded successfully!');
      setTerminationLetterModal(null);
      setUploadFile(null);
      loadDrivers();
    } catch (error) {
      console.error('Error uploading termination letter:', error);
      toast.error('Failed to upload termination letter');
    } finally {
      setUploading(false);
    }
  }

  async function handleDeleteTerminationLetter(driverId: string) {
    if (!confirm('Are you sure you want to delete this termination letter?')) return;

    try {
      setUploading(true);
      const { error } = await supabase
        .from('drivers')
        .update({ termination_letter_url: null })
        .eq('id', driverId);

      if (error) throw error;

      toast.success('Termination letter deleted successfully!');
      if (terminationLetterModal) {
        setTerminationLetterModal({ ...terminationLetterModal, currentUrl: null });
      }
      loadDrivers();
    } catch (error) {
      console.error('Error deleting termination letter:', error);
      toast.error('Failed to delete termination letter');
    } finally {
      setUploading(false);
    }
  }

  function openTerminationLetterModal(driverId: string, driverName: string, currentUrl: string | null) {
    setTerminationLetterModal({ driverId, driverName, currentUrl });
    setUploadFile(null);
  }

  function closeTerminationLetterModal() {
    setTerminationLetterModal(null);
    setUploadFile(null);
  }

  // Handle batch delete
  const handleBatchDelete = async () => {
    if (selectedDriverIds.size === 0) return;
    
    try {
      const { error } = await supabase
        .from('drivers')
        .delete()
        .in('id', Array.from(selectedDriverIds));
      
      if (error) throw error;
      
      toast.success(`${selectedDriverIds.size} driver(s) deleted successfully!`);
      setSelectedDriverIds(new Set());
      loadDrivers();
    } catch (error) {
      console.error('Error deleting drivers:', error);
      toast.error('Failed to delete drivers');
    }
  };

  // Handle marking drivers for office visit
  const handleMarkForOfficeVisit = async () => {
    if (selectedDriverIds.size === 0) return;
    if (!officeVisitReason.trim()) {
      toast.error('Please provide a reason for the office visit');
      return;
    }
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('drivers')
        .update({ 
          needs_office_visit: true,
          office_visit_reason: officeVisitReason,
          office_visit_marked_at: new Date().toISOString(),
          office_visit_marked_by: user.id,
          office_visit_completed_at: null,
          office_visit_completed_by: null
        })
        .in('id', Array.from(selectedDriverIds));
      
      if (error) throw error;
      
      toast.success(`${selectedDriverIds.size} driver(s) marked for office visit`);
      setSelectedDriverIds(new Set());
      setShowOfficeVisitModal(false);
      setOfficeVisitReason('');
      loadDrivers();
    } catch (error) {
      console.error('Error marking drivers for office visit:', error);
      toast.error('Failed to mark drivers for office visit');
    }
  };

  // Handle marking drivers as visited (completed office visit)
  const handleMarkAsVisited = async () => {
    if (selectedDriverIds.size === 0) return;
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('drivers')
        .update({ 
          needs_office_visit: false,
          office_visit_completed_at: new Date().toISOString(),
          office_visit_completed_by: user.id
        })
        .in('id', Array.from(selectedDriverIds));
      
      if (error) throw error;
      
      toast.success(`${selectedDriverIds.size} driver(s) marked as visited`);
      setSelectedDriverIds(new Set());
      loadDrivers();
    } catch (error) {
      console.error('Error marking drivers as visited:', error);
      toast.error('Failed to mark drivers as visited');
    }
  };

  // Handle batch status change
  const handleBatchStatusChange = async (newStatus: string) => {
    if (selectedDriverIds.size === 0) return;
    
    try {
      const { error } = await supabase
        .from('drivers')
        .update({ 
          driver_status: newStatus,
          updated_at: new Date().toISOString() 
        })
        .in('id', Array.from(selectedDriverIds));
      
      if (error) throw error;
      
      const statusLabel = newStatus === 'active' ? 'Active' : 
                         newStatus === 'inactive' ? 'New Drivers' : 
                         newStatus === 'vacation' ? 'Vacation' : 'Terminated';
      
      toast.success(`${selectedDriverIds.size} driver(s) moved to ${statusLabel}`);
      setSelectedDriverIds(new Set());
      setShowMoveToModal(false);
      loadDrivers();
    } catch (error) {
      console.error('Error updating driver status:', error);
      toast.error('Failed to update driver status');
    }
  };

  // Toggle company group collapse
  const toggleCompanyCollapse = (companyCode: string) => {
    const newCollapsed = new Set(collapsedCompanies);
    if (newCollapsed.has(companyCode)) {
      newCollapsed.delete(companyCode);
    } else {
      newCollapsed.add(companyCode);
    }
    setCollapsedCompanies(newCollapsed);
  };

  // Toggle status group collapse
  const toggleStatusCollapse = (status: string) => {
    const newCollapsed = new Set(collapsedStatuses);
    if (newCollapsed.has(status)) {
      newCollapsed.delete(status);
    } else {
      newCollapsed.add(status);
    }
    setCollapsedStatuses(newCollapsed);
  };

  // Status group configuration
  const statusGroups = {
    active: {
      label: `${companyFilter && companyNames[companyFilter] ? companyNames[companyFilter].toUpperCase() : ''} DRIVERS / ACTIVE`,
      color: 'from-green-50 to-green-100 hover:from-green-100 hover:to-green-200 border-green-500'
    },
    inactive: {
      label: 'NEW DRIVERS',
      color: 'from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-200 border-blue-500'
    },
    vacation: {
      label: 'VACATION',
      color: 'from-orange-50 to-orange-100 hover:from-orange-100 hover:to-orange-200 border-orange-500'
    },
    terminated: {
      label: 'TERMINATED DRIVERS',
      color: 'from-purple-50 to-purple-100 hover:from-purple-100 hover:to-purple-200 border-purple-500'
    }
  };

  useEffect(() => {
    loadDrivers();
  }, [companyFilter]);

  async function loadDrivers() {
    try {
      // Get current equipment assignments
      const { data: assignments } = await supabase
        .from('equipment_assignments')
        .select('driver_id, truck_id')
        .eq('is_current', true);

      // Get truck details for assignments
      const truckIds = assignments?.map(a => a.truck_id).filter(Boolean) || [];
      let trucksMap: Record<string, any> = {};
      
      if (truckIds.length > 0) {
        const { data: trucksData } = await supabase
          .from('trucks')
          .select('id, unit_number, make, model')
          .in('id', truckIds);
        
        if (trucksData) {
          trucksMap = trucksData.reduce((acc, truck) => {
            acc[truck.id] = truck;
            return acc;
          }, {} as Record<string, any>);
        }
      }

      // Get company ID if filtering by company
      let companyId = null;
      if (companyFilter) {
        const { data: companyData } = await supabase
          .from('companies')
          .select('id')
          .eq('code', companyFilter.toUpperCase())
          .single();
        
        if (companyData) {
          companyId = companyData.id;
        }
      }

      let driversQuery = supabase.from('drivers').select('*, companies(name, code)').order('last_name', { ascending: true });
      
      // Filter by company if specified
      if (companyId) {
        driversQuery = driversQuery.eq('company_id', companyId);
      }

      const { data: driversData, error: driversError } = await driversQuery;

      if (driversError) throw driversError;

      // Merge driver data with truck assignments
      const driversWithTrucks = (driversData || []).map(driver => {
        const assignment = assignments?.find(a => a.driver_id === driver.id);
        return {
          ...driver,
          truck: assignment?.truck_id ? trucksMap[assignment.truck_id] : null
        };
      });

      setDrivers(driversWithTrucks);
      console.log('Loaded drivers:', driversWithTrucks.length, 'Company filter:', companyFilter);
      console.log('Driver DOBs:', driversWithTrucks.slice(0, 5).map(d => ({ 
        name: `${d.first_name} ${d.last_name}`, 
        date_of_birth: d.date_of_birth,
        birthday: d.birthday 
      })));
    } catch (error) {
      console.error('Error loading drivers:', error);
    } finally {
      setLoading(false);
    }
  }

  const statusConfig = {
    active: {
      label: 'Active',
      color: 'bg-green-100 text-green-700',
      description: 'Driver is actively working'
    },
    inactive: {
      label: 'New Drivers',
      color: 'bg-blue-100 text-blue-700',
      description: 'New driver in the system'
    },
    vacation: {
      label: 'Vacation',
      color: 'bg-orange-100 text-orange-700',
      description: 'Driver is on vacation'
    },
    on_leave: {
      label: 'On Leave',
      color: 'bg-yellow-100 text-yellow-700',
      description: 'Driver is on leave (medical, personal, etc.)'
    },
    terminated: {
      label: 'Terminated',
      color: 'bg-purple-100 text-purple-700',
      description: 'Driver employment has been terminated'
    }
  };

  const filteredDrivers = drivers.filter(driver => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = !searchTerm || 
      `${driver.first_name || ''} ${driver.last_name || ''}`.toLowerCase().includes(searchLower) ||
      (driver.cdl_number || '').toLowerCase().includes(searchLower) ||
      (driver.email || '').toLowerCase().includes(searchLower) ||
      (driver.phone || '').toLowerCase().includes(searchLower);

    return matchesSearch;
  });

  // Group drivers by company OR status depending on companyFilter
  const driversByCompany = companyFilter ? {} : filteredDrivers.reduce((acc, driver) => {
    const companyCode = driver.companies?.code || 'UNASSIGNED';
    if (!acc[companyCode]) {
      acc[companyCode] = [];
    }
    acc[companyCode].push(driver);
    return acc;
  }, {} as Record<string, Driver[]>);

  // Group drivers by status for company-specific pages
  const driversByStatus = companyFilter ? filteredDrivers.reduce((acc, driver) => {
    const status = (driver.driver_status || 'active').toLowerCase();
    if (!acc[status]) {
      acc[status] = [];
    }
    acc[status].push(driver);
    return acc;
  }, {} as Record<string, Driver[]>) : {};

  // Sort drivers alphabetically within each status group
  Object.keys(driversByStatus).forEach(status => {
    driversByStatus[status].sort((a, b) => {
      const nameA = `${a.first_name || ''} ${a.last_name || ''}`.toLowerCase();
      const nameB = `${b.first_name || ''} ${b.last_name || ''}`.toLowerCase();
      return nameA.localeCompare(nameB);
    });
  });

  // Debug logging for status groups
  if (companyFilter) {
    console.log('Drivers by status:', {
      active: driversByStatus['active']?.length || 0,
      inactive: driversByStatus['inactive']?.length || 0,
      vacation: driversByStatus['vacation']?.length || 0,
      terminated: driversByStatus['terminated']?.length || 0,
      total: filteredDrivers.length
    });
    
    // Log all unique status values found
    const uniqueStatuses = [...new Set(filteredDrivers.map(d => d.driver_status || 'null'))];
    console.log('Unique status values found:', uniqueStatuses);
    
    // Log drivers with unexpected status values
    const unexpectedDrivers = filteredDrivers.filter(d => {
      const status = d.driver_status || 'active';
      return !['active', 'inactive', 'vacation', 'terminated'].includes(status);
    });
    if (unexpectedDrivers.length > 0) {
      console.log('Drivers with unexpected status values:', unexpectedDrivers.map(d => ({
        name: `${d.first_name} ${d.last_name}`,
        status: d.driver_status
      })));
    }
  }

  // Sort companies: alphabetically by name, with Unassigned always last
  const sortedCompanies = Object.keys(driversByCompany).sort((a, b) => {
    if (a === 'UNASSIGNED') return 1;
    if (b === 'UNASSIGNED') return -1;
    const nameA = companyNames[a.toLowerCase()] || a;
    const nameB = companyNames[b.toLowerCase()] || b;
    return nameA.localeCompare(nameB);
  });

  // Always show all 4 status groups for company pages, even if empty
  const sortedStatuses = companyFilter 
    ? ['active', 'inactive', 'vacation', 'terminated']
    : Object.keys(driversByStatus).sort((a, b) => {
        const order = ['active', 'inactive', 'vacation', 'terminated'];
        return order.indexOf(a) - order.indexOf(b);
      });

  const isExpiringSoon = (date: string) => {
    if (!date) return false;
    const expirationDate = new Date(date);
    const today = new Date();
    const daysUntilExpiration = Math.ceil((expirationDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return daysUntilExpiration <= 30 && daysUntilExpiration > 0;
  };

  const isExpired = (date: string) => {
    if (!date) return false;
    return new Date(date) < new Date();
  };


  const handleDeleteDriver = async () => {
    if (!driverToAction) return;

    try {
      // First unassign any truck
      if (driverToAction.truck) {
        await supabase
          .from('equipment_assignments')
          .update({ is_current: false, released_at: new Date().toISOString() })
          .eq('driver_id', driverToAction.id)
          .eq('is_current', true);
      }

      // Delete the driver
      const { error } = await supabase
        .from('drivers')
        .delete()
        .eq('id', driverToAction.id);

      if (error) throw error;

      toast.success('Driver deleted successfully!');
      setShowDeleteConfirm(false);
      setDriverToAction(null);
      loadDrivers();
    } catch (error) {
      console.error('Error deleting driver:', error);
      toast.error('Failed to delete driver');
      setShowDeleteConfirm(false);
      setDriverToAction(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {companyFilter && (
        <div className="mb-4">
          <h1 className="text-2xl font-bold text-slate-900">{companyNames[companyFilter] || 'Company'} Drivers</h1>
          <p className="text-slate-600 mt-1">Manage drivers for {companyNames[companyFilter] || 'this company'}</p>
        </div>
      )}
      
      <div className="flex items-center justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search drivers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <button 
          onClick={() => navigate('/drivers/create')}
          className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
        >
          <Plus className="w-5 h-5" />
          Add Driver
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" style={{ minWidth: '1400px' }}>
            <tbody>
              {/* Render by status groups for company-specific pages */}
              {companyFilter && sortedStatuses.map((status) => {
                const statusDrivers = driversByStatus[status] || [];
                const statusGroup = statusGroups[status as keyof typeof statusGroups];
                const isCollapsed = collapsedStatuses.has(status);
                
                return (
                  <React.Fragment key={status}>
                    {/* Status Header Row - Monday.com Style */}
                    <tr>
                      <td colSpan={status === 'terminated' ? 12 : 15} className="p-0">
                        <div 
                          className={`flex items-center justify-between px-4 py-3 cursor-pointer transition-all duration-200 bg-gradient-to-r ${statusGroup?.color || 'from-slate-50 to-slate-100'} border-l-4`}
                          onClick={() => toggleStatusCollapse(status)}
                        >
                          <div>
                            <h3 className="font-bold text-slate-900 text-base">{statusGroup?.label || status.toUpperCase()}</h3>
                            <p className="text-xs text-slate-600">
                              {statusDrivers.length} {statusDrivers.length === 1 ? 'driver' : 'drivers'}
                            </p>
                          </div>
                          <div className={`transform transition-transform duration-200 ${isCollapsed ? 'rotate-180' : ''}`}>
                            <ChevronDown className="w-5 h-5 text-slate-600" />
                          </div>
                        </div>
                      </td>
                    </tr>
                    
                    {/* Column Headers Row */}
                    {!isCollapsed && (
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="px-3 py-2 w-10">
                          <input
                            type="checkbox"
                            checked={statusDrivers.length > 0 && statusDrivers.every(d => selectedDriverIds.has(d.id))}
                            onChange={() => {
                              const allSelected = statusDrivers.every(d => selectedDriverIds.has(d.id));
                              const newSet = new Set(selectedDriverIds);
                              statusDrivers.forEach(d => {
                                if (allSelected) {
                                  newSet.delete(d.id);
                                } else {
                                  newSet.add(d.id);
                                }
                              });
                              setSelectedDriverIds(newSet);
                            }}
                            className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500 cursor-pointer"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </th>
                        <th onClick={() => handleSort('name')} className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100">Driver {sortField === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                        {status !== 'terminated' && (
                          <th onClick={() => handleSort('fuel_card_number')} className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100">Fuel Card {sortField === 'fuel_card_number' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                        )}
                        <th onClick={() => handleSort('cdl_number')} className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100">CDL Number {sortField === 'cdl_number' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                        <th onClick={() => handleSort('cdl_state')} className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100">CDL State {sortField === 'cdl_state' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                        <th onClick={() => handleSort('years_of_experience')} className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100">Years Exp {sortField === 'years_of_experience' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                        <th onClick={() => handleSort('date_of_birth')} className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100">DOB {sortField === 'date_of_birth' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                        <th onClick={() => handleSort('cdl_expiration')} className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100">CDL Exp {sortField === 'cdl_expiration' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                        {status !== 'terminated' && (
                          <>
                            <th onClick={() => handleSort('medical_card_expiration')} className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100">Medical Exp {sortField === 'medical_card_expiration' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                            <th onClick={() => handleSort('mvr_expiration_date')} className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100">MVR Exp {sortField === 'mvr_expiration_date' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                          </>
                        )}
                        <th onClick={() => handleSort('hire_date')} className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100">Hire Date {sortField === 'hire_date' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                        {status === 'terminated' && (
                          <>
                            <th onClick={() => handleSort('termination_date')} className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100">Term Date {sortField === 'termination_date' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                            <th onClick={() => handleSort('termination_reason')} className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100">Term Reason {sortField === 'termination_reason' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Term Letter</th>
                          </>
                        )}
                        <th onClick={() => handleSort('driver_status')} className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100">Status {sortField === 'driver_status' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                      </tr>
                    )}
                    
                    {/* Driver Rows */}
                    {!isCollapsed && statusDrivers.sort((a, b) => {
                      if (!sortField) return 0;
                      
                      let aVal: any = sortField === 'name' ? `${a.first_name} ${a.last_name}` : (a as any)[sortField];
                      let bVal: any = sortField === 'name' ? `${b.first_name} ${b.last_name}` : (b as any)[sortField];
                      
                      if (aVal === null || aVal === undefined) aVal = '';
                      if (bVal === null || bVal === undefined) bVal = '';
                      
                      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
                      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
                      
                      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
                      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
                      return 0;
                    }).map((driver) => {
                const cdlExpiring = isExpiringSoon(driver.cdl_expiration);
                const cdlExpired = isExpired(driver.cdl_expiration);
                const medicalExpiring = isExpiringSoon(driver.medical_card_expiration);
                const medicalExpired = isExpired(driver.medical_card_expiration);

                return (
                  <tr 
                    key={driver.id} 
                    className={`border-b border-slate-200 hover:bg-blue-50 transition-colors cursor-pointer group ${
                      selectedDriverIds.has(driver.id) ? 'bg-blue-50' : ''
                    } ${(driver as any).needs_office_visit ? 'animate-blink-bg' : ''}`}
                    style={{height: '36px'}}
                  >
                    <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedDriverIds.has(driver.id)}
                        onChange={() => toggleDriverSelection(driver.id)}
                        className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500 cursor-pointer"
                      />
                    </td>
                    <td className="px-3 py-2 cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/drivers/${driver.id}`)}>
                      <span className="font-medium text-slate-900 text-sm">
                        {driver.first_name} {driver.last_name}
                      </span>
                    </td>
                    {status !== 'terminated' && (
                      <td className="px-3 py-2">
                        <span className="text-slate-700 text-xs font-mono">{(driver as any).fuel_card_number || '-'}</span>
                      </td>
                    )}
                    <td className="px-3 py-2">
                      <span className="text-slate-700 text-xs font-mono">{driver.cdl_number || '-'}</span>
                    </td>
                    <td className="px-3 py-2">
                      <span className="text-slate-700 text-sm">{(driver as any).cdl_state || '-'}</span>
                    </td>
                    <td className="px-3 py-2">
                      <span className="text-slate-700 text-sm">{(driver as any).years_of_experience || '-'}</span>
                    </td>
                    <td className="px-3 py-2">
                      <span className="text-slate-700 text-xs">
                        {formatDateNoTZ((driver as any).date_of_birth || (driver as any).birthday)}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1">
                        {cdlExpired && <AlertCircle className="w-3 h-3 text-red-600" />}
                        {cdlExpiring && !cdlExpired && <AlertCircle className="w-3 h-3 text-orange-600" />}
                        <span className={`text-xs ${
                          cdlExpired ? 'text-red-700 font-semibold' :
                          cdlExpiring ? 'text-orange-700 font-semibold' :
                          'text-slate-700'
                        }`}>
                          {formatDateNoTZ(driver.cdl_expiration)}
                        </span>
                      </div>
                    </td>
                    {status !== 'terminated' && (
                      <>
                        <td className="px-3 py-2">
                          {driver.medical_card_expiration ? (
                            <div className="flex items-center gap-1">
                              {medicalExpired && <AlertCircle className="w-3 h-3 text-red-600" />}
                              {medicalExpiring && !medicalExpired && <AlertCircle className="w-3 h-3 text-orange-600" />}
                              <span className={`text-xs ${
                                medicalExpired ? 'text-red-700 font-semibold' :
                                medicalExpiring ? 'text-orange-700 font-semibold' :
                                'text-slate-700'
                              }`}>
                                {formatDateNoTZ(driver.medical_card_expiration)}
                              </span>
                            </div>
                          ) : (
                            <span className="text-slate-400 text-xs">-</span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {(driver as any).mvr_expiration_date ? (
                            <div className="flex items-center gap-1">
                              {isExpired((driver as any).mvr_expiration_date) && <AlertCircle className="w-3 h-3 text-red-600" />}
                              {isExpiringSoon((driver as any).mvr_expiration_date) && !isExpired((driver as any).mvr_expiration_date) && <AlertCircle className="w-3 h-3 text-orange-600" />}
                              <span className={`text-xs ${
                                isExpired((driver as any).mvr_expiration_date) ? 'text-red-700 font-semibold' :
                                isExpiringSoon((driver as any).mvr_expiration_date) ? 'text-orange-700 font-semibold' :
                                'text-slate-700'
                              }`}>
                                {formatDateNoTZ((driver as any).mvr_expiration_date)}
                              </span>
                            </div>
                          ) : (
                            <span className="text-slate-400 text-xs">-</span>
                          )}
                        </td>
                      </>
                    )}
                    <td className="px-3 py-2">
                      <span className="text-slate-700 text-xs">
                        {formatDateNoTZ(driver.hire_date)}
                      </span>
                    </td>
                    {status === 'terminated' && (
                      <>
                        <td className="px-3 py-2">
                          <span className="text-slate-700 text-xs">
                            {formatDateNoTZ((driver as any).termination_date)}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <span className="text-slate-700 text-xs">
                            {(driver as any).termination_reason || '-'}
                          </span>
                        </td>
                        <td className="px-3 py-2 cursor-pointer hover:bg-slate-100 transition-colors" onClick={(e) => { e.stopPropagation(); openTerminationLetterModal(driver.id, `${driver.first_name} ${driver.last_name}`, (driver as any).termination_letter_url); }}>
                          <div className="flex items-center justify-center gap-2">
                            <FileText className="w-4 h-4 text-slate-400" />
                            {(driver as any).termination_letter_url && (
                              <div className="relative">
                                <div className="absolute -top-1 -right-1 bg-blue-600 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                                  1
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                      </>
                    )}
                    <td className="px-0 py-0">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedDriver(driver);
                          setShowStatusModal(true);
                        }}
                        className={`w-full h-full flex items-center justify-center gap-1 px-3 py-2 text-xs font-medium cursor-pointer hover:opacity-80 transition-opacity ${
                          statusConfig[(driver.driver_status || 'active') as keyof typeof statusConfig]?.color || 'bg-green-100 text-green-700'
                        }`}
                      >
                        <Circle className="w-1.5 h-1.5 fill-current" />
                        {statusConfig[(driver.driver_status || 'active') as keyof typeof statusConfig]?.label || 'Active'}
                      </button>
                    </td>
                  </tr>
                );
                    })}
                  </React.Fragment>
                );
              })}

              {/* Render by company groups for "All Drivers" page */}
              {!companyFilter && sortedCompanies.map((companyCode, index) => {
                const companyDrivers = driversByCompany[companyCode];
                const companyName = companyCode === 'UNASSIGNED' 
                  ? 'Unassigned' 
                  : (companyNames[companyCode.toLowerCase()] || companyCode);
                const isCollapsed = collapsedCompanies.has(companyCode);
                
                // Dynamic color scheme for companies
                const colorSchemes = [
                  { bg: 'bg-gradient-to-r from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-200', border: 'border-blue-500', icon: 'bg-blue-500', chevron: 'text-blue-600' },
                  { bg: 'bg-gradient-to-r from-purple-50 to-purple-100 hover:from-purple-100 hover:to-purple-200', border: 'border-purple-500', icon: 'bg-purple-500', chevron: 'text-purple-600' },
                  { bg: 'bg-gradient-to-r from-green-50 to-green-100 hover:from-green-100 hover:to-green-200', border: 'border-green-500', icon: 'bg-green-500', chevron: 'text-green-600' },
                  { bg: 'bg-gradient-to-r from-orange-50 to-orange-100 hover:from-orange-100 hover:to-orange-200', border: 'border-orange-500', icon: 'bg-orange-500', chevron: 'text-orange-600' },
                  { bg: 'bg-gradient-to-r from-teal-50 to-teal-100 hover:from-teal-100 hover:to-teal-200', border: 'border-teal-500', icon: 'bg-teal-500', chevron: 'text-teal-600' },
                  { bg: 'bg-gradient-to-r from-pink-50 to-pink-100 hover:from-pink-100 hover:to-pink-200', border: 'border-pink-500', icon: 'bg-pink-500', chevron: 'text-pink-600' },
                  { bg: 'bg-gradient-to-r from-indigo-50 to-indigo-100 hover:from-indigo-100 hover:to-indigo-200', border: 'border-indigo-500', icon: 'bg-indigo-500', chevron: 'text-indigo-600' },
                ];
                const unassignedScheme = { bg: 'bg-gradient-to-r from-slate-50 to-slate-100 hover:from-slate-100 hover:to-slate-200', border: 'border-slate-400', icon: 'bg-slate-400', chevron: 'text-slate-600' };
                
                // Determine if this is unassigned (case-insensitive check)
                const isUnassigned = companyCode.toUpperCase() === 'UNASSIGNED' || !companyCode || companyCode === '';
                const colorScheme = isUnassigned ? unassignedScheme : colorSchemes[index % colorSchemes.length];
                
                // Get company initials for icon
                const getInitials = (name: string) => {
                  const words = name.split(' ').filter(w => w.length > 0);
                  if (words.length >= 2) {
                    return (words[0][0] + words[1][0]).toUpperCase();
                  }
                  return name.substring(0, 2).toUpperCase();
                };
                
                return (
                  <React.Fragment key={companyCode}>
                    {/* Company Header Row - Monday.com Style */}
                    <tr>
                      <td colSpan={12} className="p-0">
                        <div 
                          className={`flex items-center justify-between px-4 py-3 cursor-pointer transition-all duration-200 ${colorScheme.bg} border-l-4 ${colorScheme.border}`}
                          onClick={() => toggleCompanyCollapse(companyCode)}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`flex items-center justify-center w-8 h-8 rounded-lg ${colorScheme.icon} text-white`}>
                              <span className="text-xs font-bold">
                                {isUnassigned ? '?' : getInitials(companyName)}
                              </span>
                            </div>
                            <div>
                              <h3 className="font-bold text-slate-900 text-base">{companyName}</h3>
                              <p className="text-xs text-slate-600">
                                {companyDrivers.length} {companyDrivers.length === 1 ? 'driver' : 'drivers'}
                              </p>
                            </div>
                          </div>
                          <div className={`transform transition-transform duration-200 ${isCollapsed ? 'rotate-180' : ''}`}>
                            <ChevronDown className={`w-5 h-5 ${colorScheme.chevron}`} />
                          </div>
                        </div>
                      </td>
                    </tr>
                    
                    {/* Column Headers Row - Monday.com Style */}
                    {!isCollapsed && (
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="px-3 py-2 w-10">
                          <input
                            type="checkbox"
                            checked={filteredDrivers.length > 0 && selectedDriverIds.size === filteredDrivers.length}
                            onChange={toggleSelectAll}
                            className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500 cursor-pointer"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </th>
                        <th onClick={() => handleSort('name')} className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100">Driver {sortField === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                        <th onClick={() => handleSort('fuel_card_number')} className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100">Fuel Card {sortField === 'fuel_card_number' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                        <th onClick={() => handleSort('cdl_number')} className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100">CDL Number {sortField === 'cdl_number' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                        <th onClick={() => handleSort('cdl_state')} className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100">CDL State {sortField === 'cdl_state' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                        <th onClick={() => handleSort('years_of_experience')} className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100">Years Exp {sortField === 'years_of_experience' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                        <th onClick={() => handleSort('date_of_birth')} className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100">DOB {sortField === 'date_of_birth' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                        <th onClick={() => handleSort('cdl_expiration')} className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100">CDL Exp {sortField === 'cdl_expiration' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                        <th onClick={() => handleSort('medical_card_expiration')} className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100">Medical Exp {sortField === 'medical_card_expiration' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                        <th onClick={() => handleSort('mvr_expiration_date')} className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100">MVR Exp {sortField === 'mvr_expiration_date' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                        <th onClick={() => handleSort('hire_date')} className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100">Hire Date {sortField === 'hire_date' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                        <th onClick={() => handleSort('driver_status')} className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100">Status {sortField === 'driver_status' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                      </tr>
                    )}
                    
                    {/* Driver Rows */}
                    {!isCollapsed && companyDrivers.sort((a, b) => {
                      if (!sortField) return 0;
                      
                      let aVal: any = sortField === 'name' ? `${a.first_name} ${a.last_name}` : (a as any)[sortField];
                      let bVal: any = sortField === 'name' ? `${b.first_name} ${b.last_name}` : (b as any)[sortField];
                      
                      if (aVal === null || aVal === undefined) aVal = '';
                      if (bVal === null || bVal === undefined) bVal = '';
                      
                      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
                      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
                      
                      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
                      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
                      return 0;
                    }).map((driver) => {
                const cdlExpiring = isExpiringSoon(driver.cdl_expiration);
                const cdlExpired = isExpired(driver.cdl_expiration);
                const medicalExpiring = isExpiringSoon(driver.medical_card_expiration);
                const medicalExpired = isExpired(driver.medical_card_expiration);

                return (
                  <tr 
                    key={driver.id} 
                    className={`border-b border-slate-200 hover:bg-blue-50 transition-colors cursor-pointer group ${
                      selectedDriverIds.has(driver.id) ? 'bg-blue-50' : ''
                    } ${(driver as any).needs_office_visit ? 'animate-blink-bg' : ''}`}
                    style={{height: '36px'}}
                  >
                    <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedDriverIds.has(driver.id)}
                        onChange={() => toggleDriverSelection(driver.id)}
                        className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500 cursor-pointer"
                      />
                    </td>
                    <td className="px-3 py-2 cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/drivers/${driver.id}`)}>
                      <span className="font-medium text-slate-900 text-sm">
                        {driver.first_name} {driver.last_name}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span className="text-slate-700 text-xs font-mono">{(driver as any).fuel_card_number || '-'}</span>
                    </td>
                    <td className="px-3 py-2">
                      <span className="text-slate-700 text-xs font-mono">{driver.cdl_number || '-'}</span>
                    </td>
                    <td className="px-3 py-2">
                      <span className="text-slate-700 text-sm">{(driver as any).cdl_state || '-'}</span>
                    </td>
                    <td className="px-3 py-2">
                      <span className="text-slate-700 text-sm">{(driver as any).years_of_experience || '-'}</span>
                    </td>
                    <td className="px-3 py-2">
                      <span className="text-slate-700 text-xs">
                        {formatDateNoTZ((driver as any).date_of_birth || (driver as any).birthday)}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1">
                        {cdlExpired && <AlertCircle className="w-3 h-3 text-red-600" />}
                        {cdlExpiring && !cdlExpired && <AlertCircle className="w-3 h-3 text-orange-600" />}
                        <span className={`text-xs ${
                          cdlExpired ? 'text-red-700 font-semibold' :
                          cdlExpiring ? 'text-orange-700 font-semibold' :
                          'text-slate-700'
                        }`}>
                          {formatDateNoTZ(driver.cdl_expiration)}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      {driver.medical_card_expiration ? (
                        <div className="flex items-center gap-1">
                          {medicalExpired && <AlertCircle className="w-3 h-3 text-red-600" />}
                          {medicalExpiring && !medicalExpired && <AlertCircle className="w-3 h-3 text-orange-600" />}
                          <span className={`text-xs ${
                            medicalExpired ? 'text-red-700 font-semibold' :
                            medicalExpiring ? 'text-orange-700 font-semibold' :
                            'text-slate-700'
                          }`}>
                            {formatDateNoTZ(driver.medical_card_expiration)}
                          </span>
                        </div>
                      ) : (
                        <span className="text-slate-400 text-xs">-</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {(driver as any).mvr_expiration_date ? (
                        <div className="flex items-center gap-1">
                          {isExpired((driver as any).mvr_expiration_date) && <AlertCircle className="w-3 h-3 text-red-600" />}
                          {isExpiringSoon((driver as any).mvr_expiration_date) && !isExpired((driver as any).mvr_expiration_date) && <AlertCircle className="w-3 h-3 text-orange-600" />}
                          <span className={`text-xs ${
                            isExpired((driver as any).mvr_expiration_date) ? 'text-red-700 font-semibold' :
                            isExpiringSoon((driver as any).mvr_expiration_date) ? 'text-orange-700 font-semibold' :
                            'text-slate-700'
                          }`}>
                            {formatDateNoTZ((driver as any).mvr_expiration_date)}
                          </span>
                        </div>
                      ) : (
                        <span className="text-slate-400 text-xs">-</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <span className="text-slate-700 text-xs">
                        {formatDateNoTZ(driver.hire_date)}
                      </span>
                    </td>
                    <td className="px-0 py-0">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedDriver(driver);
                          setShowStatusModal(true);
                        }}
                        className={`w-full h-full flex items-center justify-center gap-1 px-3 py-2 text-xs font-medium cursor-pointer hover:opacity-80 transition-opacity ${
                          statusConfig[(driver.driver_status || 'active') as keyof typeof statusConfig]?.color || 'bg-green-100 text-green-700'
                        }`}
                      >
                        <Circle className="w-1.5 h-1.5 fill-current" />
                        {statusConfig[(driver.driver_status || 'active') as keyof typeof statusConfig]?.label || 'Active'}
                      </button>
                    </td>
                  </tr>
                );
                    })}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {filteredDrivers.length === 0 && (
        <div className="text-center py-12">
          <p className="text-slate-500">No drivers found</p>
        </div>
      )}

      {/* Floating Action Bar - Monday.com Style */}
      {selectedDriverIds.size > 0 && (
        <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-50 animate-in slide-in-from-bottom-4">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-200 px-6 py-4 flex items-center gap-6">
            {/* Counter */}
            <div className="flex items-center gap-3">
              <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">
                {selectedDriverIds.size}
              </div>
              <span className="font-semibold text-slate-900">
                {selectedDriverIds.size === 1 ? 'Driver selected' : 'Drivers selected'}
              </span>
            </div>

            {/* Divider */}
            <div className="h-8 w-px bg-slate-300"></div>

            {/* Edit Button - Only for Safety, Superadmin, Admin */}
            {selectedDriverIds.size === 1 && ['Safety', 'Superadmin', 'Admin'].includes(profile?.role || '') && (
              <button
                onClick={() => {
                  const driverId = Array.from(selectedDriverIds)[0];
                  navigate(`/drivers/edit/${driverId}`);
                }}
                className="flex items-center gap-2 px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <Edit className="w-5 h-5" />
                <span className="font-medium">Edit</span>
              </button>
            )}

            {/* Office Visit Button - Show only if selected drivers don't need office visit */}
            {(() => {
              const selectedDriversNeedVisit = Array.from(selectedDriverIds).every(id => {
                const driver = drivers.find(d => d.id === id);
                return driver && (driver as any).needs_office_visit;
              });
              
              if (selectedDriversNeedVisit) {
                // Show "Mark as Visited" button if all selected drivers need office visit
                return (
                  <button
                    onClick={handleMarkAsVisited}
                    className="flex items-center gap-2 px-4 py-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                  >
                    <Building2 className="w-5 h-5" />
                    <span className="font-medium">Mark as Visited</span>
                  </button>
                );
              } else {
                // Show "Office Visit" button if drivers don't need office visit
                return (
                  <button
                    onClick={() => setShowOfficeVisitModal(true)}
                    className="flex items-center gap-2 px-4 py-2 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                  >
                    <Building2 className="w-5 h-5" />
                    <span className="font-medium">Office Visit</span>
                  </button>
                );
              }
            })()}

            {/* Move To Button */}
            <button
              onClick={() => setShowMoveToModal(true)}
              className="flex items-center gap-2 px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
              <span className="font-medium">Move to</span>
            </button>

            {/* Delete Button */}
            <button
              onClick={handleBatchDelete}
              className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <Trash2 className="w-5 h-5" />
              <span className="font-medium">Delete</span>
            </button>

            {/* Close Button */}
            <button
              onClick={() => setSelectedDriverIds(new Set())}
              className="ml-2 p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Move To Status Modal */}
      {showMoveToModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-sm w-full">
            <div className="p-4 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-900">Move to Status</h2>
                <button
                  onClick={() => setShowMoveToModal(false)}
                  className="p-1 hover:bg-slate-100 rounded transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <p className="text-xs text-slate-600 mt-1">
                {selectedDriverIds.size} {selectedDriverIds.size === 1 ? 'driver' : 'drivers'} selected
              </p>
            </div>

            <div className="p-2">
              <div className="space-y-1">
                {/* Active */}
                <button
                  onClick={() => handleBatchStatusChange('active')}
                  className="w-full px-3 py-2.5 text-left rounded transition-all flex items-center gap-2 hover:bg-green-50"
                >
                  <Circle className="w-2 h-2 fill-current text-green-700" />
                  <span className="font-medium text-sm">Active Drivers</span>
                </button>

                {/* New Drivers */}
                <button
                  onClick={() => handleBatchStatusChange('inactive')}
                  className="w-full px-3 py-2.5 text-left rounded transition-all flex items-center gap-2 hover:bg-blue-50"
                >
                  <Circle className="w-2 h-2 fill-current text-blue-700" />
                  <span className="font-medium text-sm">New Drivers</span>
                </button>

                {/* Vacation */}
                <button
                  onClick={() => handleBatchStatusChange('vacation')}
                  className="w-full px-3 py-2.5 text-left rounded transition-all flex items-center gap-2 hover:bg-orange-50"
                >
                  <Circle className="w-2 h-2 fill-current text-orange-700" />
                  <span className="font-medium text-sm">Vacation</span>
                </button>

                {/* Terminated */}
                <button
                  onClick={() => handleBatchStatusChange('terminated')}
                  className="w-full px-3 py-2.5 text-left rounded transition-all flex items-center gap-2 hover:bg-purple-50"
                >
                  <Circle className="w-2 h-2 fill-current text-purple-700" />
                  <span className="font-medium text-sm">Terminated Drivers</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Driver Confirmation */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        title="Delete Driver"
        message={driverToAction
          ? `Are you sure you want to delete ${driverToAction.first_name} ${driverToAction.last_name}? This action cannot be undone.`
          : ''}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDeleteDriver}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setDriverToAction(null);
        }}
        type="danger"
      />

      {/* Status Change Modal - Monday.com Style */}
      {showStatusModal && selectedDriver && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-sm w-full">
            <div className="p-4 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-900">Driver Status</h2>
                <button
                  onClick={() => {
                    setShowStatusModal(false);
                    setSelectedDriver(null);
                  }}
                  className="p-1 hover:bg-slate-100 rounded transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="p-2">
              <div className="space-y-1">
                {/* Active Drivers */}
                <button
                  onClick={async () => {
                    try {
                      const { error } = await supabase
                        .from('drivers')
                        .update({ 
                          driver_status: 'active',
                          updated_at: new Date().toISOString() 
                        })
                        .eq('id', selectedDriver.id);

                      if (error) throw error;

                      toast.success('Driver status updated to Active');
                      setShowStatusModal(false);
                      setSelectedDriver(null);
                      loadDrivers();
                    } catch (error) {
                      console.error('Error updating driver status:', error);
                      toast.error('Failed to update driver status');
                    }
                  }}
                  className={`w-full px-3 py-2.5 text-left rounded transition-all flex items-center gap-2 ${
                    (selectedDriver.driver_status || 'active') === 'active'
                      ? 'bg-green-100 text-green-700'
                      : 'hover:bg-slate-50'
                  }`}
                >
                  <Circle className="w-2 h-2 fill-current" />
                  <span className="font-medium text-sm">Active Drivers</span>
                  {(selectedDriver.driver_status || 'active') === 'active' && (
                    <svg className="w-4 h-4 ml-auto" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>

                {/* New Drivers */}
                <button
                  onClick={async () => {
                    try {
                      const { error } = await supabase
                        .from('drivers')
                        .update({ 
                          driver_status: 'inactive',
                          updated_at: new Date().toISOString() 
                        })
                        .eq('id', selectedDriver.id);

                      if (error) throw error;

                      toast.success('Driver status updated to New Driver');
                      setShowStatusModal(false);
                      setSelectedDriver(null);
                      loadDrivers();
                    } catch (error) {
                      console.error('Error updating driver status:', error);
                      toast.error('Failed to update driver status');
                    }
                  }}
                  className={`w-full px-3 py-2.5 text-left rounded transition-all flex items-center gap-2 ${
                    selectedDriver.driver_status === 'inactive'
                      ? 'bg-blue-100 text-blue-700'
                      : 'hover:bg-slate-50'
                  }`}
                >
                  <Circle className="w-2 h-2 fill-current" />
                  <span className="font-medium text-sm">New Drivers</span>
                  {selectedDriver.driver_status === 'inactive' && (
                    <svg className="w-4 h-4 ml-auto" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>

                {/* Vacation */}
                <button
                  onClick={async () => {
                    try {
                      const { error } = await supabase
                        .from('drivers')
                        .update({ 
                          driver_status: 'vacation',
                          updated_at: new Date().toISOString() 
                        })
                        .eq('id', selectedDriver.id);

                      if (error) throw error;

                      toast.success('Driver status updated to Vacation');
                      setShowStatusModal(false);
                      setSelectedDriver(null);
                      loadDrivers();
                    } catch (error) {
                      console.error('Error updating driver status:', error);
                      toast.error('Failed to update driver status');
                    }
                  }}
                  className={`w-full px-3 py-2.5 text-left rounded transition-all flex items-center gap-2 ${
                    selectedDriver.driver_status === 'vacation'
                      ? 'bg-orange-100 text-orange-700'
                      : 'hover:bg-slate-50'
                  }`}
                >
                  <Circle className="w-2 h-2 fill-current" />
                  <span className="font-medium text-sm">Vacation</span>
                  {selectedDriver.driver_status === 'vacation' && (
                    <svg className="w-4 h-4 ml-auto" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>

                {/* Terminated Drivers */}
                <button
                  onClick={async () => {
                    try {
                      const { error } = await supabase
                        .from('drivers')
                        .update({ 
                          driver_status: 'terminated',
                          updated_at: new Date().toISOString() 
                        })
                        .eq('id', selectedDriver.id);

                      if (error) throw error;

                      toast.success('Driver status updated to Terminated');
                      setShowStatusModal(false);
                      setSelectedDriver(null);
                      loadDrivers();
                    } catch (error) {
                      console.error('Error updating driver status:', error);
                      toast.error('Failed to update driver status');
                    }
                  }}
                  className={`w-full px-3 py-2.5 text-left rounded transition-all flex items-center gap-2 ${
                    selectedDriver.driver_status === 'terminated'
                      ? 'bg-purple-100 text-purple-700'
                      : 'hover:bg-slate-50'
                  }`}
                >
                  <Circle className="w-2 h-2 fill-current" />
                  <span className="font-medium text-sm">Terminated Drivers</span>
                  {selectedDriver.driver_status === 'terminated' && (
                    <svg className="w-4 h-4 ml-auto" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Termination Letter Upload Modal */}
      {terminationLetterModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4 rounded-t-xl">
              <h3 className="text-xl font-bold text-white">Termination Letter - {terminationLetterModal.driverName}</h3>
            </div>

            <div className="p-6 space-y-4">
              {/* Current Letter */}
              {terminationLetterModal.currentUrl && (
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Current Letter
                  </label>
                  <div className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                    <FileText className="w-4 h-4 text-blue-600" />
                    <SignedLink
                      stored={terminationLetterModal.currentUrl}
                      className="text-sm text-blue-600 hover:underline flex-1 truncate"
                    >
                      Termination Letter
                    </SignedLink>
                    <button
                      onClick={() => handleDeleteTerminationLetter(terminationLetterModal.driverId)}
                      className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                      title="Delete letter"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* File Upload */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  {terminationLetterModal.currentUrl ? 'Replace Letter' : 'Upload Letter'}
                </label>
                <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        setUploadFile(e.target.files[0]);
                      }
                    }}
                    className="hidden"
                    id="termination-letter-upload"
                  />
                  <label
                    htmlFor="termination-letter-upload"
                    className="cursor-pointer flex flex-col items-center"
                  >
                    <Upload className="w-8 h-8 text-slate-400 mb-2" />
                    <span className="text-sm text-slate-600">Click to upload letter</span>
                    <span className="text-xs text-slate-400 mt-1">PDF, JPG, PNG up to 10MB</span>
                  </label>
                  {uploadFile && (
                    <div className="mt-4 text-left">
                      <p className="text-sm font-medium text-slate-700 mb-2">Selected file:</p>
                      <div className="flex items-center justify-between bg-white px-3 py-2 rounded border border-slate-200">
                        <span className="text-sm text-slate-700">{uploadFile.name}</span>
                        <button
                          type="button"
                          onClick={() => setUploadFile(null)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={closeTerminationLetterModal}
                  className="flex-1 px-6 py-3 border-2 border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors font-semibold"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleTerminationLetterUpload(terminationLetterModal.driverId)}
                  disabled={uploading || !uploadFile}
                  className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploading ? 'Uploading...' : 'Upload'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Office Visit Modal */}
      {showOfficeVisitModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="bg-gradient-to-r from-orange-600 to-orange-700 px-6 py-4 rounded-t-xl">
              <h3 className="text-xl font-bold text-white">Mark for Office Visit</h3>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Reason for Office Visit <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={officeVisitReason}
                  onChange={(e) => setOfficeVisitReason(e.target.value)}
                  placeholder="Enter reason why driver needs to come to Chicago office..."
                  rows={4}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>

              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                <p className="text-sm text-orange-800">
                  <strong>{selectedDriverIds.size}</strong> driver{selectedDriverIds.size !== 1 ? 's' : ''} will be marked for office visit. 
                  Their name will appear with a red background in the dispatch board.
                </p>
              </div>
            </div>

            <div className="px-6 py-4 bg-slate-50 rounded-b-xl flex gap-3">
              <button
                onClick={() => {
                  setShowOfficeVisitModal(false);
                  setOfficeVisitReason('');
                }}
                className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleMarkForOfficeVisit}
                disabled={!officeVisitReason.trim()}
                className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Mark for Visit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
